import { createFileRoute } from '@tanstack/react-router';
import { CodeBlock, DocTable, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/idle-timer')({
  component: IdleTimerPage,
});

function IdleTimerPage() {
  return (
    <>
      <div className={styles.breadcrumb}>SDK</div>
      <h1 className={styles.pageTitle}>Idle Timer</h1>
      <p className={styles.pageSubtitle}>
        Automatically end sessions when users step away — with a customizable
        warning and countdown built into the <code className={styles.mono}>&lt;pixel-stream&gt;</code>{' '}
        component.
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="overview">
          Overview
        </h2>
        <p className={styles.prose}>
          Idle streaming sessions consume GPU resources even when nobody is
          watching. The idle timer detects inactivity, warns the user, and ends
          the session if they don't respond. It's off by default — add the{' '}
          <code className={styles.mono}>idle-timer</code> attribute to enable it.
        </p>
        <p className={styles.prose}>
          The state machine runs entirely inside the component. It observes
          user input sent to the data channel (mouse, keyboard, touch, gamepad)
          and walks through four states:
        </p>
        <div className={styles.timeline}>
          <pre className={styles.timelinePre}>{`streaming ─── [idle-watch-duration] ─── grace ─── [idle-warning-duration] ─── countdown ─── [idle-countdown-duration → 0] ─── stop('Idle timer expiry')
                     ↑                             │                                   │
                     └───────────── any user interaction ────────────────────────────────┘`}</pre>
        </div>
        <p className={styles.prose}>
          Any genuine user input — even a mouse wiggle — resets the timer back
          to watching. The countdown dialog itself is a small floating card;
          the rest of the viewport is transparent and passes pointer events
          through to the stream.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="minimal-example">
          Minimal Example
        </h2>
        <p className={styles.prose}>
          The smallest configuration: enable it and accept the defaults.
        </p>
        <CodeBlock language="HTML">
{`<pixel-stream controls idle-timer
    api-endpoint="api.interlucent.ai">
</pixel-stream>

<script src="https://cdn.interlucent.ai/pixel-stream/latest/pixel-stream.iife.min.js"></script>
<script>
  document.querySelector('pixel-stream').admissionToken = 'YOUR_TOKEN';
</script>`}
        </CodeBlock>
        <p className={styles.prose}>
          After 5 minutes of inactivity the stream turns grayscale. 5 seconds
          later, a frosted-glass dialog appears with a 30-second countdown.
          If the user doesn't interact, the session ends with reason{' '}
          <code className={styles.mono}>Idle timer expiry</code>.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="attributes">
          Attributes
        </h2>
        <DocTable
          columns={[
            { header: 'Attribute', accessor: 'attr', mono: true, nowrap: true },
            { header: 'Type', accessor: 'type' },
            { header: 'Default', accessor: 'default' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            {
              attr: 'idle-timer',
              type: 'boolean',
              default: 'off',
              description: 'Enable the idle timer. Present = enabled, absent = disabled.',
            },
            {
              attr: 'idle-audible-warning',
              type: 'boolean',
              default: 'off',
              description:
                'Play a short 880Hz beep when entering the grace state. Useful as an accessibility cue, but disabled by default since autoplay policies may block it before user gesture.',
            },
            {
              attr: 'idle-watch-duration',
              type: 'ms',
              default: '300000',
              description:
                'How long the user must be idle (no input to the stream) before the grace state begins. Five minutes by default.',
            },
            {
              attr: 'idle-warning-duration',
              type: 'ms',
              default: '5000',
              description:
                'How long the grace state lasts (grayscale filter, no dialog yet) before the countdown dialog appears.',
            },
            {
              attr: 'idle-countdown-duration',
              type: 'seconds',
              default: '30',
              description:
                'How long the countdown runs before the session is terminated. The dialog shows a ticking number and a depleting progress bar.',
            },
          ]}
        />

        <InfoCallout variant="info">
          <strong>Reactive updates:</strong> these attributes can be toggled
          dynamically at any time. Setting <code className={styles.mono}>idle-timer</code>{' '}
          to false during an active countdown immediately returns the timer to
          the off state.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="states">
          States
        </h2>
        <p className={styles.prose}>
          The current state is exposed via the{' '}
          <code className={styles.mono}>idle-state</code> attribute on the host
          element. You can target it from external CSS.
        </p>
        <DocTable
          columns={[
            { header: 'State', accessor: 'state', mono: true, nowrap: true },
            { header: 'Visual', accessor: 'visual' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            {
              state: 'off',
              visual: 'Normal',
              description:
                'Timer disabled, or the stream is not active. No monitoring.',
            },
            {
              state: 'watching',
              visual: 'Normal',
              description:
                'Stream is active. The component is monitoring lastUserInteraction but nothing visual has changed.',
            },
            {
              state: 'grace',
              visual: 'Grayscale',
              description:
                'User has been idle long enough. Stream is desaturated (via CSS filter) and the optional beep fires once. No dialog yet.',
            },
            {
              state: 'countdown',
              visual: 'Grayscale + dialog',
              description:
                'The frosted-glass dialog is visible with a ticking number and progress bar. Dismissing returns to watching; reaching 0 terminates the session.',
            },
          ]}
        />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="events">
          Events
        </h2>
        <p className={styles.prose}>
          Listen for state transitions and per-second countdown ticks to
          integrate the idle timer with your app's UI or analytics.
        </p>
        <CodeBlock language="JavaScript">
{`const ps = document.querySelector('pixel-stream');

ps.addEventListener('idle-state-change', (e) => {
  console.log(\`Idle: \${e.detail.oldState} → \${e.detail.newState}\`);
});

ps.addEventListener('idle-countdown-tick', (e) => {
  console.log(\`\${e.detail.remaining} seconds remaining\`);
});`}
        </CodeBlock>
        <DocTable
          columns={[
            { header: 'Event', accessor: 'event', mono: true, nowrap: true },
            { header: 'Detail', accessor: 'detail' },
            { header: 'Fires', accessor: 'fires' },
          ]}
          data={[
            {
              event: 'idle-state-change',
              detail: '{ oldState, newState }',
              fires: 'On every transition between states.',
            },
            {
              event: 'idle-countdown-tick',
              detail: '{ remaining }',
              fires: 'Every second while in the countdown state, including the initial tick.',
            },
          ]}
        />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="customization">
          Customizing the Overlay
        </h2>
        <p className={styles.prose}>
          The built-in dialog adapts to the user's color scheme preference and
          should fit most applications. If you need a fully custom idle UI,
          slot your own content into the{' '}
          <code className={styles.mono}>idle-overlay</code> slot and use the{' '}
          <code className={styles.mono}>idle-state</code> attribute as a CSS
          trigger.
        </p>
        <CodeBlock language="HTML">
{`<pixel-stream controls idle-timer api-endpoint="api.interlucent.ai">
  <div slot="idle-overlay" class="my-idle-wrap">
    <div class="my-idle-card">
      <h3>Still there?</h3>
      <span class="my-countdown">30</span>
      <button data-idle-action="dismiss">I'm here</button>
    </div>
  </div>
</pixel-stream>

<style>
  .my-idle-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .my-idle-card {
    pointer-events: auto;
    background: rgba(20, 20, 24, 0.85);
    backdrop-filter: blur(16px);
    border-radius: 12px;
    padding: 1.5rem 2rem;
    color: white;
    text-align: center;
  }
  /* Only show during countdown */
  pixel-stream[idle-state="countdown"] .my-idle-wrap {
    display: flex;
  }
  pixel-stream:not([idle-state="countdown"]) .my-idle-wrap {
    display: none;
  }
</style>`}
        </CodeBlock>

        <p className={styles.prose}>
          Two conventions make the slotted overlay work with the component's
          built-in behavior:
        </p>
        <ul className={styles.prose}>
          <li>
            Any element with{' '}
            <code className={styles.mono}>data-idle-action="dismiss"</code> acts
            as the reset button — the component attaches a click handler
            automatically.
          </li>
          <li>
            Subscribe to{' '}
            <code className={styles.mono}>idle-countdown-tick</code> to update
            your own countdown display. The built-in tick also updates its own
            internal elements, but your slotted content is on its own.
          </li>
        </ul>

        <InfoCallout variant="info">
          <strong>Pass-through pointer events:</strong> both the built-in and
          custom overlays should keep their container{' '}
          <code className={styles.mono}>pointer-events: none</code> and only
          give <code className={styles.mono}>pointer-events: auto</code> to the
          dialog card itself. This lets the user "wiggle" the mouse anywhere on
          the stream to reset the timer, not just inside the dialog.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="leave-reason">
          Session Termination
        </h2>
        <p className={styles.prose}>
          When the countdown reaches zero, the component calls{' '}
          <code className={styles.mono}>stop('Idle timer expiry')</code>. This
          propagates to both ends of the session:
        </p>
        <ul className={styles.prose}>
          <li>
            The <code className={styles.mono}>status-change</code> event fires
            with <code className={styles.mono}>newStatus: 'ended'</code> and{' '}
            <code className={styles.mono}>failureReason: 'Idle timer expiry'</code>.
          </li>
          <li>
            The session's leave signal includes the same reason string, which
            is visible in server-side session logs for analytics and billing
            attribution.
          </li>
          <li>
            Other agents in the session (e.g. in a collaboration scenario) see
            this agent depart with that reason.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="react">
          React
        </h2>
        <p className={styles.prose}>
          The React wrapper exposes all idle timer attributes as props and all
          events as callbacks.
        </p>
        <CodeBlock language="TypeScript">
{`import { PixelStream } from '@interlucent/pixel-stream-react';

function App() {
  return (
    <PixelStream
      admissionToken={token}
      idleTimer
      idleAudibleWarning
      idleWatchDuration={300_000}
      idleWarningDuration={5_000}
      idleCountdownDuration={30}
      onIdleStateChange={(e) => {
        console.log(\`\${e.detail.oldState} → \${e.detail.newState}\`);
      }}
      onIdleCountdownTick={(e) => {
        console.log(\`\${e.detail.remaining}s remaining\`);
      }}
    />
  );
}`}
        </CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="notes">
          Implementation Notes
        </h2>
        <ul className={styles.prose}>
          <li>
            <strong>What counts as interaction:</strong> any input the component
            sends to the streamer via the WebRTC data channel (mouse, keyboard,
            touch, gamepad, XR). <code className={styles.mono}>MouseEnter</code>{' '}
            and <code className={styles.mono}>MouseLeave</code> are explicitly
            excluded — they are boundary signals, not user activity.
          </li>
          <li>
            <strong>Starts and stops with streaming:</strong> the timer only
            runs while <code className={styles.mono}>status === 'streaming'</code>.
            It is reset to <code className={styles.mono}>off</code> on any other
            status transition, including reconnection and recovery.
          </li>
          <li>
            <strong>Theme-aware:</strong> the built-in dialog respects{' '}
            <code className={styles.mono}>prefers-color-scheme</code> and
            switches between dark and light frosted glass automatically.
          </li>
        </ul>
      </section>
    </>
  );
}
